import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import RoleBadge from '../layout/RoleBadge'
import { useConsultants } from '../../hooks/useConsultants'
import ActivityFeed from '../common/ActivityFeed.jsx'
import { ProjectsByStatusPie, BudgetUtilizationBar, PriorityDistributionBar, ProjectProgressLine } from '../common/DashboardCharts.jsx'

// --- NEW: placeholder Icon ---
const ExternalLinkIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
  </svg>
);

import { useProjects } from '../../hooks/useProjects.jsx'
import { useUsers } from '../../hooks/useUsers.js'
import AIActionGuide from '../ai/AIActionGuide.jsx'
import AgentProposals from './AgentProposals.jsx'
import EmailIntakeLog from './EmailIntakeLog.jsx'
import IdLookup from '../admin/IdLookup.jsx'
import ReadyToCloseAlert from './ReadyToCloseAlert.jsx'
import { useTranslation } from 'react-i18next';

function AdminDashboard() {
  const { t } = useTranslation('dashboards');
  const navigate = useNavigate()
  const { user } = useAuth()
  const { projects, loading: projectsLoading, getProjectStats } = useProjects()
  const { users, loading: usersLoading } = useUsers()

  const projectStats = getProjectStats;

  // Pending consultant approvals (Applied / Under Review). Activation itself happens in the
  // Onboarding pipeline (it's gated on the full KYC checklist), so this panel surfaces who's
  // waiting and links there — it doesn't fake an approve action that would bypass the checklist.
  const {
    pendingApprovals,
    loading: consultantsLoading
  } = useConsultants();

  // Filter for only pending ones
  const pending = Array.isArray(pendingApprovals) ? pendingApprovals.filter(a => a.status === 'pending') : [];

  const stats = [
    {
      name: t('total_projects'),
      value: projectStats.total,
      icon: '📊',
      color: 'bg-blue-500'
    },
    {
      name: t('active_users'),
      value: Array.isArray(users) ? users.filter(u => u.is_active || u.status === 'Active').length : 0,
      icon: '👥',
      color: 'bg-green-500'
    },
    {
      // Active = Planning + In Progress. The PM dashboard counted only In Progress, so the two
      // screens reported different numbers for the same projects; it now uses this definition.
      name: t('active_projects'),
      value: projectStats.active,
      icon: '⏳',
      color: 'bg-yellow-500'
    },
    {
      // This has always been the sum of APPROVED budgets, while the card said "Total Budget" --
      // the same figure a PM's budget chart now plots, under the same word.
      name: t('approved_budget'),
      value: '$' + (projectStats.totalBudget / 1000).toFixed(0) + 'K',
      icon: '💰',
      color: 'bg-purple-500'
    }
  ]

  // Activity feed is now handled by the ActivityFeed component

  return (
    <div className="p-6 space-y-6">
      {/* AI Copilot action guide */}
      <AIActionGuide />
      {/* Prepared by the agent, not yet done — usually from an email. */}
      <AgentProposals />
      {/* What arrived by email and what became of it — collapsed until something is wrong. */}
      <EmailIntakeLog />
      {/* Projects whose milestones are all complete — prompt to close them */}
      <ReadyToCloseAlert />
      {/* header */}
      <div className="bg-linear-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('admin_title')}</h1>
            <p className="mt-1 opacity-90">{t('admin_subtitle')}</p>
          </div>
          <div className="text-right">
            <p className="text-sm opacity-90">{t('welcome_back')}</p>
            <p className="text-lg font-semibold">{user?.full_name || user?.name}</p>
            <RoleBadge role={user?.role} />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="card p-6 hover-lift">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>

              </div>
              <div className="text-2xl">{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Dashboard Charts (Phase C2) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProjectsByStatusPie projects={projects} />
        <BudgetUtilizationBar projects={projects} />
        {/* An Admin sees every project a PM sees, and until now saw two fewer charts about them. */}
        <PriorityDistributionBar projects={projects} />
        <ProjectProgressLine projects={projects} />
      </div>

      {/* Admin ID Overview — trace by Consultant / Project / Contract ID */}
      <IdLookup />

      {/* --- NEW PENDING CONSULTANT APPROVALS SECTION --- */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{t('pending_consultant_approvals')}</h3>
          <p className="text-sm text-gray-500 mt-1">{t('review_shared_consultants')}</p>
        </div>

        {consultantsLoading ? (
          <p className="p-6 text-gray-500">{t('loading_approvals')}</p>
        ) : pending.length === 0 ? (
          <p className="p-6 text-gray-500">{t('no_pending_approvals')}</p>
        ) : (
          <div className="divide-y divide-gray-200">
            {pending.map(item => (
              <div key={item.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">

                {/* Profile Info — read the REAL user fields (full_name / specialization);
                    the old avatar/name/title/linkedInUrl fields never existed on this object. */}
                <div className="flex items-center space-x-4 mb-3 sm:mb-0">
                  <div className="w-10 h-10 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center font-bold">
                    {(item.profile.full_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{item.profile.full_name || t('unnamed_consultant')}</div>
                    <div className="text-sm text-gray-600">{item.profile.specialization || item.profile.email || '—'}</div>
                    <div className="text-xs text-amber-600">{item.profile.consultant_status || 'Applied'}</div>
                  </div>
                </div>

                {/* Activation is gated on the onboarding checklist — send the Admin there to
                    review + activate, rather than a shortcut that would bypass the KYC gate. */}
                <div className="flex flex-col sm:flex-row gap-2 mt-2 sm:mt-0 shrink-0">
                  <button
                    onClick={() => navigate('/onboarding')}
                    className="px-3 py-1 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-all w-full sm:w-auto flex items-center justify-center gap-1"
                  >
                    Review in Onboarding <ExternalLinkIcon />
                  </button>
                </div>

              </div>
            ))}
          </div>
        )}
      </div>
      {/* -------------------------------------------------- */}


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* System Overview */}
        <div className="lg:col-span-2 card">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('system_overview')}</h3>
          </div>
          <div className="p-6">
            {(usersLoading || projectsLoading) ? (
              <p className="p-6 text-gray-500">{t('loading_system')}</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-4">{t('user_distribution')}</h4>
                  <div className="space-y-3">
                    {Array.isArray(users) && users.slice(0, 5).map((user, index) => (
                      <div key={user.id || index} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 rounded-full bg-linear-to-r from-blue-500 to-indigo-600 flex items-center justify-center">
                            <span className="text-xs font-medium text-white">{user.full_name ? user.full_name[0] : 'U'}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
                            <p className="text-xs text-gray-500">{user.role}</p>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${user.is_active || user.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                          {user.is_active ? 'Active' : (user.status || 'Inactive')}
                        </span>
                      </div>
                    ))}
                    {(!Array.isArray(users) || users.length === 0) && <p className="text-sm text-gray-500">{t('no_users_found')}</p>}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-4">{t('project_status')}</h4>
                  <div className="space-y-3">
                    {Array.isArray(projects) && projects.slice(0, 5).map((project, index) => (
                      <div
                        key={project.id || index}
                        onClick={() => project.id && navigate(`/projects/${project.id}`)}
                        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded-lg px-2 -mx-2 py-1"
                        title={t('open_project')}
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 hover:text-blue-600">{project.name}</p>
                          <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${project.progress || 0}%` }}
                            ></div>
                          </div>
                        </div>
                        <span className="ml-4 text-sm text-gray-600">{project.progress || 0}%</span>
                      </div>
                    ))}
                    {(!Array.isArray(projects) || projects.length === 0) && <p className="text-sm text-gray-500">{t('no_projects_found')}</p>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activities - Using ActivityFeed Component */}
        <ActivityFeed title={t('recent_activity')} maxItems={6} />
      </div>
    </div>
  )
}

export default AdminDashboard
